export interface Params {
  React: any // React
  createRoot: any // ReactDom/client/createRoot
  MFRuntime: any // MFRuntime
}

const win = globalThis as any

class VueAdapter {
  private static libs: Params = {
    React: win.EMP_ADAPTER_REACT.React,
    createRoot: win.EMP_ADAPTER_REACT.createRoot,
    MFRuntime: win.EMP_SHARE_RUNTIME.MFRuntime,
  }

  private static options = {
    loadingClassName: 'state-loading',
    errorClassName: 'state-error',
    slotClassName: 'vue-slot',
    loadingText: '加载中',
    errorText: '加载失败',
  }

  static step(libs: Params) {
    this.libs = libs
  }

  // 初始化 远程组件
  static init(params: any) {
    this.libs.MFRuntime.init(params)
  }

  private static vueMixin: any = {
    name: 'VueAdapter',
    data() {
      return {
        state: 0, // 0加载中，1加载完成，-1加载失败
      }
    },
    props: {
      remote: {
        type: String,
        required: true,
      },
      loadingText: {
        type: String,
        default: VueAdapter.options.loadingText,
      },
      errorText: {
        type: String,
        default: VueAdapter.options.errorText,
      },
      watchRender: {
        type: Boolean,
        default: true,
      },
    },
    watch: {
      $attrs: {
        deep: true,
        handler() {
          const that = this as any
          if (that.watchRender) {
            that.renderReact()
          }
        },
      },
      $route: {
        deep: true,
        handler() {
          const that = this as any
          if (that.watchRender) {
            that.renderReact()
          }
        },
      },
    },
    mounted() {
      const {remote} = this
      try {
        VueAdapter.libs.MFRuntime.loadRemote(remote).then((res: any) => {
          if (res && res.default && this.$refs.el) {
            this.state = 1

            this.$reactComponent = res.default
            this.$reactApp = VueAdapter.libs.createRoot(this.$refs.el)
            this.renderReact()
          } else {
            this.state = -1
          }
        })
      } catch (e) {
        this.state = -1
      }
    },
  }

  private static vueMethods = {
    getRouter() {
      let router = {}
      const that = this as any
      if (that.$router) {
        const {path, query, params} = that.$route
        router = {
          path,
          push: (path: string) => {
            that.$router.push(path)
          },
          replace: (path: string) => {
            that.$router.replace(path)
          },
          goBack: () => {
            that.$router.back()
          },
          query,
          params,
        }
      }

      return router
    },
    renderReact() {
      const that = this as any
      if (that.$reactComponent) {
        that.$reactApp.render(VueAdapter.libs.React.createElement(that.$reactComponent, that.getParams()))
      }
    },
  }

  /**
   * 注册 Vue2Adapter 组件
   * @param Vue 项目 Vue 对象
   * @param vueInitParams slots 实例化 Vue 需要的参数，如 store, vuex, i18n 等等
   * @constructor
   */
  private static vue2Adapter = (Vue: any, vueInitParams?: Record<string, any>) => {
    return {
      ...this.vueMixin,
      render(h: any) {
        const loading = this.$slots.loading || h('span', null, [this.loadingText])
        const error = this.$slots.error || h('span', null, [this.errorText])
        return h(
          'div',
          {
            ref: 'el',
            class: [
              this.state === 0 && VueAdapter.options.loadingClassName,
              this.state === -1 && VueAdapter.options.errorClassName,
              vueInitParams?.className,
            ],
            attrs: {
              'data-remote': this.remote,
            },
          },
          [this.state === 0 ? loading : null, this.state === -1 ? error : null],
        )
      },
      methods: {
        ...this.vueMethods,
        getParams(): Record<string, any> {
          // 包含了父作用域中不作为 prop 被识别 (且获取) 的 attribute 绑定 (class 和 style 除外)
          const that = this as any
          const attrs = that.$attrs
          // 包含了父作用域中的 (不含 .native 修饰器的) v-on 事件监听器
          const listeners = {...that.$listeners}

          /**
           * 在 Vue 3 的虚拟 DOM 中，事件监听器现在只是以 on 为前缀的 attribute，这样它就成为了 $attrs 对象的一部分，
           * 因此 $listeners 被移除了
           * 这里兼容 vue3，click => onClick
           */
          Object.entries(listeners).forEach(([key, value]) => {
            const onKey = 'on' + key.substring(0, 1).toUpperCase() + key.substring(1)
            listeners[onKey] = value
          })
          // 自定义表格
          const schema = that.schema
          // router 封装
          const router = this.getRouter()
          // vue slots
          let slots: any = null
          const VueSlot = that.$slots.default
          if (VueSlot && VueSlot.length) {
            that.$reactAppSlots = slots = new Vue({
              ...vueInitParams,
              render: (h: any) => {
                return VueSlot.length > 1 ? h('div', {class: VueAdapter.options.slotClassName}, VueSlot) : VueSlot[0]
              },
            })
          }

          const params = {
            ...attrs,
            ...listeners,
            schema,
            router,
            slots,
            _adapter: 'Vue2',
            'data-remote': attrs.remote,
          }

          // console.log('EmpReactAdepter', 'getParams', params)

          return params
        },
      },
      beforeDestroy() {
        if (this.$reactApp) {
          if (this.$reactAppSlots && this.$reactAppSlots.$el) {
            // 判断 slots 是否挂载、已挂载销毁
            this.$reactAppSlots.$destroy()
            this.$reactAppSlots.$el.remove()
          }
          this.$reactApp.unmount()
        }
      },
    } as any
  }

  /**
   * 注册 Vue3Adapter 组件
   * @param Vue
   * @param vuePlugins slots 创建 vue 的插件、如 router, i18n 等等
   * @constructor
   */
  private static vue3Adapter = (Vue: any, vuePlugins?: Record<string, any>) => {
    const {h, createApp} = Vue
    return {
      ...this.vueMixin,
      render() {
        const loading = (this.$slots.loading && h(this.$slots.loading)) || h('span', null, [this.loadingText])
        const error = (this.$slots.error && h(this.$slots.error)) || h('span', null, [this.errorText])
        return h(
          'div',
          {
            ref: 'el',
            class: [
              this.state === 0 && VueAdapter.options.loadingClassName,
              this.state === -1 && VueAdapter.options.errorClassName,
              vuePlugins?.className,
            ],
            'data-remote': this.remote,
          },
          [this.state === 0 ? loading : null, this.state === -1 ? error : null],
        )
      },
      methods: {
        ...this.vueMethods,
        getParams(): Record<string, any> {
          // 包含了父作用域中不作为 prop 被识别 (且获取) 的 attribute 绑定 (class 和 style 除外)
          const that = this as any
          const attrs = that.$attrs
          // 包含了父作用域中的 (不含 .native 修饰器的) v-on 事件监听器
          const listeners = that.$listeners
          // 自定义表格
          const schema = that.schema
          // router 封装
          const router = this.getRouter()
          // vue slots
          let slots: any = null
          const VueSlot = that.$slots.default
          if (VueSlot) {
            that.$reactAppSlots = slots = createApp(VueSlot)

            if (vuePlugins) {
              Object.values(vuePlugins).forEach(plugin => {
                slots.use(plugin)
              })
            }
          }

          const params = {
            ...attrs,
            ...listeners,
            schema,
            router,
            slots,
            _adapter: 'Vue3',
          }

          // console.log('EmpReactAdepter', 'getParams', params)

          return params
        },
      },
      beforeUnmount() {
        if (this.$reactApp) {
          // 这里用 _container 判断 slots 组件是否已挂载
          if (this.$reactAppSlots && this.$reactAppSlots._container) {
            this.$reactAppSlots.unmount()
          }
          this.$reactApp.unmount()
        }
      },
    } as any
  }

  static adapter = (...rest: any) => {
    if (rest[0]?.h) {
      return this.vue3Adapter.apply(null, rest)
    }
    return this.vue2Adapter.apply(null, rest)
  }
}

export {VueAdapter}
